import { useEffect, useRef } from 'react'

import DropdownSearchBar from 'modules/core/components/DropdownSearchBar'
import i18n from 'modules/core/lib/i18n'

import { PerDiemRate, PerDiemRateOptions } from 'modules/expenses/hooks/usePerDiemForm'

interface Props {
  selectedRate?: PerDiemRate
  perDiemRatesOptions: PerDiemRateOptions
  error?: string
  mandatory?: boolean
  readonly?: boolean
  loading?: boolean
  onSelect: (perDiemRateId: number | null) => void
}

const PerDiemRateField = ({
  selectedRate,
  perDiemRatesOptions,
  error,
  mandatory = false,
  readonly,
  loading,
  onSelect,
}: Props) => {
  const hasAutoSelected = useRef(false)

  const parseRateValue = (value: string | number): number | null => {
    return typeof value === 'string' ? Number.parseInt(value, 10) || null : value
  }

  // Auto-select the first rate if there is only one option and no rate is selected
  useEffect(() => {
    if (
      perDiemRatesOptions.length === 1 &&
      (!selectedRate || selectedRate.id === null) &&
      !hasAutoSelected.current
    ) {
      const singleRate = perDiemRatesOptions[0]
      if (singleRate?.value !== undefined) {
        hasAutoSelected.current = true
        onSelect(parseRateValue(singleRate.value))
      }
    }
  }, [perDiemRatesOptions, selectedRate, onSelect])

  return (
    <DropdownSearchBar
      name={selectedRate?.label || ''}
      label={i18n.t('budgets.forms.external_resource.per_diem_field.label')}
      hideLabel
      data={perDiemRatesOptions}
      error={error}
      selectLabel={i18n.t('budgets.forms.external_resource.per_diem_field.options.empty')}
      placeholder={i18n.t('budgets.forms.external_resource.per_diem_field.options.empty')}
      searchLabel={i18n.t('common.search')}
      sortLabel={i18n.t('common.name')}
      labelNotFound={i18n.t('budgets.forms.external_resource.per_diem_field.search.not_found')}
      disabled={readonly}
      loading={loading}
      autofocus={false}
      mandatory={mandatory}
      onSelectEmpty={!mandatory ? () => onSelect(null) : null}
      onSelect={(item) => {
        hasAutoSelected.current = true
        onSelect(parseRateValue(item.value))
      }}
    />
  )
}

export default PerDiemRateField
